import clsx from "clsx";
import { useRef, type ReactNode, type RefObject } from "react";
import { Spinner } from "@/components/ui/spinner";
import { usePullToRefresh } from "./usePullToRefresh";

export interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => void | Promise<void>;
  scrollRef?: RefObject<HTMLElement | null>;
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * Scroll container with pull-down-to-refresh (touch and pointer).
 */
export function PullToRefresh({
  children,
  onRefresh,
  scrollRef: externalScrollRef,
  disabled = false,
  className,
  "data-testid": testId = "pull-to-refresh",
}: PullToRefreshProps) {
  const localScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = externalScrollRef ?? localScrollRef;
  const { pullDistance, isRefreshing, pullProgress } = usePullToRefresh({
    scrollRef,
    onRefresh,
    disabled,
  });

  const indicatorHeight = isRefreshing ? 48 : pullDistance;
  const showIndicator = indicatorHeight > 0;

  return (
    <div
      className={clsx("relative min-h-0 flex-1 overflow-hidden", className)}
      data-testid={testId}
    >
      <div
        ref={scrollRef as RefObject<HTMLDivElement>}
        className="h-full overflow-y-auto overscroll-y-contain touch-pan-y"
      >
        <div
          className={clsx(
            "min-h-full",
            !isRefreshing && pullDistance === 0 && "transition-[padding] duration-200 ease-out"
          )}
          style={{ paddingTop: showIndicator ? indicatorHeight : 0 }}
        >
          {showIndicator ? (
            <div
              className="flex items-center justify-center"
              style={{
                height: indicatorHeight,
                marginTop: -indicatorHeight,
                opacity: isRefreshing ? 1 : 0.35 + pullProgress * 0.65,
              }}
              data-testid={`${testId}-indicator`}
              aria-hidden={!isRefreshing}
            >
              <Spinner size="sm" label={isRefreshing ? "Refreshing" : undefined} />
            </div>
          ) : null}
          {children}
        </div>
      </div>
    </div>
  );
}
